'use client';

import {
	BigNum,
	CustomizedCadenceBulkAccountLoader,
	OracleClient,
	OraclePriceData,
	// OracleSource,
	PRICE_PRECISION_EXP,
	PollingVelocityClientAccountSubscriber,
	PublicKey,
	ZERO,
	getOracleClient,
} from '@velocity-exchange/sdk';
import { ENUM_UTILS, MarketId, UIMarket } from '@velocity-exchange/common';
import { useEffect, useMemo, useRef } from 'react';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { publishMarketLiveUpdate } from 'src/stores/marketLiveState';
import { MarketListeningSelection } from '../stores/useMarketStateStore';
import useVelocityClientIsReady from './useVelocityClientIsReady';
import useMarketsForDlobToTrack from './useMarketsForDlobToTrack';
import { dlog } from 'src/dev';
import { usePerpMarketsLookup } from './perpMarkets/usePerpMarketsLookup';
import { useSpotMarketsLookup } from './spotMarkets/useSpotMarketsLookup';

// TOOD: fix in SDK, then revert back to importing from SDK
export class OracleSource {
	static readonly PYTH_LAZER = { pythLazer: {} };
	static readonly PYTH_LAZER_1K = { pythLazer1K: {} };
	static readonly PYTH_LAZER_1M = { pythLazer1M: {} };
	static readonly PYTH_LAZER_STABLE_COIN = { pythLazerStableCoin: {} };
}

export type FormattedOraclePriceData = {
	price: number;
	slot: number;
	confidence: number;
	twap?: number;
	twapConfidence?: number;
	hasSufficientNumberOfDataPoints: OraclePriceData['hasSufficientNumberOfDataPoints'];
	maxPrice?: number;
};

const ORACLE_CLIENT_KEYS = Object.values(OracleSource).map(
	(value) => Object.keys(value)[0]
);
type OracleClientsMap = Map<string, OracleClient | undefined>;

// Oracle prices stream in over the account subscriber's websocket; the bulk
// loader poll is only a fallback safety net, so it runs at a slow cadence.
const ORACLE_POLL_FALLBACK_FREQUENCY_MS = 30_000;

const useSyncOracleDataWithRpc = () => {
	const perpMarketsLookup = usePerpMarketsLookup();
	const spotMarketsLookup = useSpotMarketsLookup();
	const connection = useVelocityStore((s) => s.connection);
	// The app always constructs a CustomizedCadenceBulkAccountLoader (see
	// generateAccountLoader) — needed here for the per-account fallback cadence.
	const bulkLoader = useVelocityStore((s) => s.connection.accountLoader) as
		| CustomizedCadenceBulkAccountLoader
		| undefined;
	const velocityClient = useVelocityStore((s) => s.velocityClient.client);
	const velocityClientIsReady = useVelocityClientIsReady();

	const oracleClientMap = useRef<OracleClientsMap>(new Map());

	const marketsToTrackWithBlockchain = useMarketsForDlobToTrack(
		MarketListeningSelection.BLOCKCHAIN
	);

	const usdcMarket = MarketId.createSpotMarket(0);

	const marketsAndAccountsToUse: {
		market: UIMarket;
		accountToUse: PublicKey;
	}[] = useMemo(() => {
		if (perpMarketsLookup.length < 2 || spotMarketsLookup.length < 2) {
			// this is the market look ups initialized with only 1 length as a default, so this is returning an empty array before the app wrapper has loaded the remote configs
			return [];
		}
		return [usdcMarket, ...marketsToTrackWithBlockchain.allMarkets].map(
			(market) => {
				const isPerp = market.isPerp;

				return {
					market: isPerp
						? UIMarket.createPerpMarket(market.marketIndex)
						: UIMarket.createSpotMarket(market.marketIndex),
					accountToUse: isPerp
						? perpMarketsLookup[market.marketIndex]?.oracle
						: spotMarketsLookup[market.marketIndex]?.oracle,
				};
			}
		);
	}, [
		perpMarketsLookup,
		spotMarketsLookup,
		marketsToTrackWithBlockchain.categories,
	]);

	// Every perp + spot market with its oracle account. The velocity client's
	// `oraclePriceUpdate` fires only for oracles it is actually subscribed to,
	// so mapping over ALL markets lets us publish rpc oracle data for every
	// client-subscribed market (not just the blockchain-tracked ones the
	// fallback poll below covers). This is what lets `useGetOraclePriceForMarket`
	// drop its direct velocity-client pull.
	const allMarketsAndAccounts: {
		market: UIMarket;
		accountToUse: PublicKey;
	}[] = useMemo(() => {
		// Same guard as marketsAndAccountsToUse above: the lookups initialize to
		// length 1 as a default (see usePerpMarketsLookup's `new Array(max > 0 ?
		// max : 1)`), so a length < 2 means the app wrapper hasn't loaded the
		// remote configs yet — bail rather than map over placeholder configs.
		if (perpMarketsLookup.length < 2 || spotMarketsLookup.length < 2) {
			return [];
		}
		const perp = perpMarketsLookup
			.filter((config) => !!config)
			.map((config) => ({
				market: UIMarket.createPerpMarket(config.marketIndex),
				accountToUse: config.oracle,
			}));
		const spot = spotMarketsLookup
			.filter((config) => !!config)
			.map((config) => ({
				market: UIMarket.createSpotMarket(config.marketIndex),
				accountToUse: config.oracle,
			}));
		return [...perp, ...spot];
	}, [perpMarketsLookup, spotMarketsLookup]);

	const bulkLoaderCallbacks = useRef<[string, PublicKey][]>([]);

	// Tracks the freshest oracle slot we've stored per market, and whether it
	// came from the VelocityClient's own subscription (whatever its transport:
	// websocket or the polling subscriber) or from this hook's fallback poll —
	// used to warn when the fallback poll is the one doing the work.
	const lastOracleUpdateRef = useRef<
		Map<string, { slot: number; source: 'subscriber' | 'fallbackPoll' }>
	>(new Map());

	// Initialize the oracle clients
	useEffect(() => {
		if (!velocityClient?.program || !connection.current) return;

		ORACLE_CLIENT_KEYS.forEach((oracleSourceKey) => {
			if (!connection.current) return;
			const oracleSource = ENUM_UTILS.toObj(oracleSourceKey);

			oracleClientMap.current.set(
				oracleSourceKey,
				getOracleClient(
					oracleSource as OracleSource,
					connection.current,
					velocityClient.program
				)
			);
		});
	}, [connection.current, velocityClient?.program]);

	const getClient = useMemo(
		() => (oracleSource: OracleSource) => {
			if (!velocityClient) return undefined;

			const oracleSourceString = ENUM_UTILS.toStr(oracleSource);
			const oracleClient = oracleClientMap.current.get(oracleSourceString);

			if (!oracleClient) {
				throw new Error(`Oracle client not found for ${oracleSourceString}`);
			}

			return oracleClient;
		},
		[velocityClient]
	);

	useEffect(() => {
		if (!connection) return;
		if (!velocityClientIsReady) return;
		if (!velocityClient) return;

		const applyOraclePriceData = (
			market: UIMarket,
			oraclePriceData: OraclePriceData | undefined,
			source: 'subscriber' | 'fallbackPoll'
		) => {
			if (oraclePriceData) {
				lastOracleUpdateRef.current.set(market.key, {
					slot: oraclePriceData.slot?.toNumber() ?? 0,
					source,
				});
			}

			if (!oraclePriceData) return;

			publishMarketLiveUpdate('rpc', market.marketId, {
				oracle: {
					price: BigNum.from(
						oraclePriceData.price ?? ZERO,
						PRICE_PRECISION_EXP
					),
					slot: oraclePriceData.slot.toNumber(),
					confidence: oraclePriceData.confidence,
					hasSufficientNumberOfDataPoints:
						oraclePriceData.hasSufficientNumberOfDataPoints ?? false,
					twap: oraclePriceData.twap
						? BigNum.from(oraclePriceData.twap, PRICE_PRECISION_EXP)
						: undefined,
					twapConfidence: oraclePriceData.twapConfidence,
					maxPrice: oraclePriceData.maxPrice
						? BigNum.from(oraclePriceData.maxPrice, PRICE_PRECISION_EXP)
						: undefined,
					raw: oraclePriceData,
				},
			});
		};

		const updateOracleDataWithAccountBuffer = (
			market: UIMarket,
			accountDataBuffer: Buffer,
			source: 'subscriber' | 'fallbackPoll'
		) => {
			const accountClient = getClient(market.market.oracleSource);
			if (!accountDataBuffer) {
				return;
			}
			const oraclePriceData =
				accountClient?.getOraclePriceDataFromBuffer(accountDataBuffer);

			applyOraclePriceData(market, oraclePriceData, source);
		};

		// The fallback poll should only ever re-deliver data the websocket
		// already gave us. If it delivers *fresher* data, the websocket oracle
		// stream isn't keeping up (or isn't running) and the fallback is
		// load-bearing — surface that so we know it can't be removed.
		const warnIfFallbackIsFresher = (
			market: UIMarket,
			oraclePriceData: OraclePriceData | undefined
		) => {
			if (!oraclePriceData) return;

			const polledSlot = oraclePriceData.slot?.toNumber() ?? 0;
			const last = lastOracleUpdateRef.current.get(market.key);

			if (!last) {
				console.warn(
					`[OracleRpcFallback] ${market.key}: fallback poll delivered the first oracle data (slot ${polledSlot}) — no websocket oracle update has arrived yet`
				);
				return;
			}

			if (polledSlot > last.slot) {
				console.warn(
					`[OracleRpcFallback] ${market.key}: fallback poll is fresher than the last stored update (polled slot ${polledSlot} > stored slot ${last.slot}, stored source: ${last.source}) — websocket oracle updates are lagging or not flowing`
				);
			}
		};

		const oracleKey = (
			pubkey: PublicKey | string,
			oracleSource: Record<string, any>
		) => `${pubkey.toString()}-${ENUM_UTILS.toStr(oracleSource)}`;

		// Primary source: oracle updates the VelocityClient's account subscriber
		// already delivers (websocket pushes, or the client's own poll in polling
		// mode) — no extra RPC cost for this hook either way. Keyed over ALL
		// markets so any oracle the client emits resolves to its market(s),
		// regardless of the market's DLOB listening selection.
		const marketsByOracle = new Map<string, UIMarket[]>();
		allMarketsAndAccounts.forEach(({ market, accountToUse }) => {
			if (!accountToUse) return;
			const key = oracleKey(accountToUse, market.market.oracleSource);
			marketsByOracle.set(key, [...(marketsByOracle.get(key) ?? []), market]);
		});

		const handleSubscriberOraclePriceUpdate = (
			publicKey: PublicKey,
			oracleSource: Record<string, any>,
			data: OraclePriceData
		) => {
			const markets = marketsByOracle.get(oracleKey(publicKey, oracleSource));
			if (!markets) return;
			markets.forEach((market) =>
				applyOraclePriceData(market, data, 'subscriber')
			);
		};

		velocityClient.eventEmitter.on(
			'oraclePriceUpdate',
			handleSubscriberOraclePriceUpdate
		);

		// Seed every client-subscribed market's oracle synchronously from the
		// velocity client's own cache.
		allMarketsAndAccounts.forEach(({ market }) => {
			const marketId = market.marketId;
			try {
				const oraclePriceData = marketId.isPerp
					? velocityClient.getOracleDataForPerpMarket(marketId.marketIndex)
					: velocityClient.getOracleDataForSpotMarket(marketId.marketIndex);
				applyOraclePriceData(market, oraclePriceData, 'subscriber');
			} catch {
				// Market not subscribed on the client yet — the listener picks it up
				// on its next oracle tick.
			}
		});

		dlog(
			`oracle_price_debugging`,
			`oracle_rpc_sync_markets_to_use`,
			marketsAndAccountsToUse.map(({ market }) => market.key).join(',')
		);

		// In polling mode the bulk loader poll IS the oracle price source, so it
		// must stay at the default cadence. In websocket mode it's only a
		// fallback safety net and runs slow.
		const pollIsFallbackOnly = !(
			velocityClient.accountSubscriber instanceof
			PollingVelocityClientAccountSubscriber
		);

		// TODO: this over-renders and runs too often
		// Register the RPC poll for each tracked oracle
		marketsAndAccountsToUse.forEach(({ market, accountToUse }) => {
			bulkLoader
				?.addAccount(accountToUse, async (accountDataBuffer) => {
					const accountClient = getClient(market.market.oracleSource);
					const oraclePriceData = accountDataBuffer
						? accountClient?.getOraclePriceDataFromBuffer(accountDataBuffer)
						: undefined;
					if (pollIsFallbackOnly) {
						warnIfFallbackIsFresher(market, oraclePriceData);
					}
					applyOraclePriceData(market, oraclePriceData, 'fallbackPoll');
				})
				.then((callbackId) => {
					bulkLoaderCallbacks.current.push([callbackId, accountToUse]);
					// addAccount clamps custom frequencies to min(custom, default),
					// so a slower-than-default fallback cadence must be forced via
					// setCustomPollingFrequency afterwards.
					if (pollIsFallbackOnly) {
						bulkLoader.setCustomPollingFrequency(
							accountToUse,
							ORACLE_POLL_FALLBACK_FREQUENCY_MS
						);
					}
				});
		});

		// Trigger a manual initial update for all markets
		marketsAndAccountsToUse.forEach(({ market, accountToUse }) => {
			const prefetchedResult = bulkLoader?.bufferAndSlotMap.get(
				accountToUse?.toString() ?? ''
			);

			if (!prefetchedResult || !prefetchedResult.buffer) return;

			updateOracleDataWithAccountBuffer(
				market,
				prefetchedResult.buffer,
				'fallbackPoll'
			);
		});

		return () => {
			velocityClient.eventEmitter.removeListener(
				'oraclePriceUpdate',
				handleSubscriberOraclePriceUpdate
			);
			// Clean up bulk loader account listeners
			if (bulkLoaderCallbacks.current.length) {
				bulkLoaderCallbacks.current.forEach(([callbackId, accountKey]) => {
					bulkLoader?.removeAccount(accountKey, callbackId);
				});
				bulkLoaderCallbacks.current = [];
			}
		};
	}, [
		connection,
		bulkLoader,
		velocityClient,
		velocityClientIsReady,
		marketsAndAccountsToUse,
		allMarketsAndAccounts,
	]);
};

export default useSyncOracleDataWithRpc;
